/**
 * Shared App Service ↔ Functions proxy timeout and SPA link-policy client timeout.
 * Client timeout is always strictly below the proxy timeout by API_PROXY_CLIENT_MARGIN_MS.
 */

export const DEFAULT_API_PROXY_TIMEOUT_MS = 90_000;

/** SPA aborts this many ms before the App Service proxy (avoids racing a 504). */
export const API_PROXY_CLIENT_MARGIN_MS = 5_000;

/** Node `setTimeout` / `setInterval` delay ceiling (signed 32-bit int max). */
export const MAX_NODE_TIMEOUT_MS = 2_147_483_647;

/**
 * @param {string | undefined} rawEnv - `API_PROXY_TIMEOUT_MS` or `VITE_API_PROXY_TIMEOUT_MS`
 * @returns {number}
 */
export function resolveApiProxyTimeoutMs(rawEnv) {
  const raw = rawEnv === undefined ? process.env.API_PROXY_TIMEOUT_MS : rawEnv;
  if (raw === undefined || String(raw).trim() === '') {
    return DEFAULT_API_PROXY_TIMEOUT_MS;
  }
  const parsed = Number(String(raw).trim());
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(
      `API_PROXY_TIMEOUT_MS must be a finite positive number (milliseconds); got ${JSON.stringify(raw)}`,
    );
  }
  if (parsed > MAX_NODE_TIMEOUT_MS) {
    throw new Error(
      `API_PROXY_TIMEOUT_MS must be <= ${MAX_NODE_TIMEOUT_MS} (Node timer delay limit); got ${JSON.stringify(raw)}`,
    );
  }
  return parsed;
}

/**
 * Client timeout for POST /link-policy — always strictly less than the proxy timeout.
 * @param {number} [proxyTimeoutMs]
 * @returns {number}
 */
export function resolveLinkPolicyClientTimeoutMs(proxyTimeoutMs = resolveApiProxyTimeoutMs()) {
  const proxy =
    Number.isFinite(proxyTimeoutMs) && proxyTimeoutMs > 0
      ? proxyTimeoutMs
      : DEFAULT_API_PROXY_TIMEOUT_MS;
  const client = proxy - API_PROXY_CLIENT_MARGIN_MS;
  if (!Number.isFinite(client) || client <= 0) {
    throw new Error(
      `link-policy client timeout must be positive; proxy timeout ${proxy} ms is too small for margin ${API_PROXY_CLIENT_MARGIN_MS} ms`,
    );
  }
  if (!(client < proxy)) {
    throw new Error('link-policy client timeout must be strictly below the API proxy timeout');
  }
  return client;
}
